import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faChevronDown, faSpinner, faArrowsUpDown, faFloppyDisk } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from 'react-i18next';
import KinoKlikIcon from "../../images/logo.png";
import { useTheme } from "../../context/ThemeContext";
import { PINNED_SECTIONS, resolveOrder, SECTION_META } from "../../config/epkSections";
import ReorderSectionsControl from "../Epk/ReorderSectionsControl";

export default function EditNavBar({ activeSection, onSectionClick, onDiscard, onSave, isSaving, sectionOrder, onReorderApply }) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isReorderOpen, setIsReorderOpen] = useState(false);
  const [isReorderDragging, setIsReorderDragging] = useState(false);
  const dropdownRef = useRef(null);

  // Single source of truth for section order/labels (client/src/config/epkSections.js).
  // The TOC follows the saved draft order so it never drifts from the rendered page.
  const order = resolveOrder(sectionOrder);
  const SECTIONS = [
    ...PINNED_SECTIONS.map((id) => ({ id, label: t(SECTION_META[id].labelKey) })),
    ...order.map((id) => ({ id, label: t(SECTION_META[id]?.labelKey || id) })),
  ];

  useEffect(() => {
    function handleClickOutside(event) {
      // Don't fight the reorder control's own drag: a drag can carry the
      // pointer outside this TOC dropdown's ref mid-gesture.
      if (isReorderDragging) return;
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isReorderDragging]);

  const handleNavClick = (id) => {
    onSectionClick(id);
    setIsDropdownOpen(false);
  };

  const currentLabel = SECTIONS.find(s => s.id === activeSection)?.label || SECTIONS[0].label;

  return (
    <div
      className="tw-fixed tw-top-0 tw-left-0 tw-right-0 tw-h-[103px] tw-bg-[#280D41] tw-shadow-[0_20px_40px_rgba(0,0,0,0.8)] tw-z-[1050] tw-flex tw-items-center tw-px-3 sm:tw-px-4 lg:tw-px-[32px] tw-gap-2 sm:tw-gap-3"
      style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
    >
      
      {/* 1. BRAND ANCHOR */}
      <div className="tw-shrink-0 tw-flex tw-items-center tw-gap-2">
        <img src={KinoKlikIcon} alt={t("KinoKlik")} className="tw-w-[44px] tw-h-[44px] lg:tw-w-[56px] lg:tw-h-[56px] tw-object-contain tw-brightness-0 tw-invert" />
        <span
          className="tw-hidden sm:tw-inline tw-text-lg tw-font-bold tw-text-white"
          style={{ fontFamily: "'Josefin Sans', sans-serif", letterSpacing: '0.08em' }}
        >
          {t("KinoKlik")}
        </span>
      </div>

      {/* 2. TABLE OF CONTENTS - DROPDOWN (all breakpoints, so it never overflows the bar) */}
      <div className="tw-flex tw-flex-1 tw-min-w-0 tw-justify-center tw-relative" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className={`tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-max-w-full tw-bg-black/30 tw-border tw-border-white/20 tw-rounded-full tw-px-4 tw-py-2 tw-font-bold tw-text-sm tw-cursor-pointer ${isLight ? 'tw-text-[#7C3AED]' : 'tw-text-[#FFB0CF]'}`}
        >
          <span className="tw-max-w-[140px] tw-truncate">{currentLabel}</span>
          <FontAwesomeIcon icon={faChevronDown} className={`tw-shrink-0 tw-transition-transform ${isDropdownOpen ? 'tw-rotate-180' : ''}`} />
        </button>

        {isDropdownOpen && (
          <div className="tw-absolute tw-top-full tw-mt-2 tw-left-1/2 tw--translate-x-1/2 tw-w-[220px] tw-bg-[#280D41] tw-border tw-border-[#FF43A7]/50 tw-rounded-xl tw-shadow-2xl tw-overflow-hidden tw-flex tw-flex-col">
            {SECTIONS.map((section) => (
              <button
                key={section.id}
                onClick={() => handleNavClick(section.id)}
                className={`tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-sm tw-transition-colors tw-bg-transparent tw-border-none tw-cursor-pointer
                  ${activeSection === section.id
                    ? (isLight ? 'tw-bg-[#7C3AED]/10 tw-text-[#7C3AED]' : 'tw-bg-[#FF43A7]/20 tw-text-[#FFB0CF]')
                    : (isLight ? 'tw-text-[#374151] hover:tw-bg-black/5 hover:tw-text-[#111827]' : 'tw-text-[rgba(240,219,255,0.8)] hover:tw-bg-white/10 hover:tw-text-white')
                  }
                `}
              >
                {section.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 3. TRAILING ACTIONS (Compact Buttons) */}
      <div className="tw-flex tw-flex-row tw-items-center tw-gap-[8px] sm:tw-gap-[12px] tw-shrink-0">

        {/* REORDER BUTTON (opens the compact TOC reorder control) */}
        <div className="tw-relative">
          <button
            onClick={() => {
              setIsDropdownOpen(false);
              setIsReorderOpen((open) => !open);
            }}
            disabled={isSaving}
            title={t("Reorder")}
            className="tw-flex tw-items-center tw-justify-center tw-h-12 tw-w-12 md:tw-w-auto md:tw-px-5 md:tw-gap-2 tw-rounded-full tw-border tw-border-white/20 tw-bg-white/10 hover:tw-bg-white/20 tw-text-white tw-transition-all tw-outline-none tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-shrink-0"
          >
            <FontAwesomeIcon icon={faArrowsUpDown} className="tw-text-lg" />
            <span className="tw-hidden md:tw-inline tw-text-sm tw-font-bold">{t("Reorder")}</span>
          </button>

          <ReorderSectionsControl
            isOpen={isReorderOpen}
            onClose={() => setIsReorderOpen(false)}
            sectionOrder={sectionOrder}
            onApply={onReorderApply}
            onDraggingChange={setIsReorderDragging}
          />
        </div>

        {/* DISCARD BUTTON  */}
        <button
          onClick={onDiscard}
          disabled={isSaving}
          title={t("Discard Changes")}
          className="tw-flex tw-items-center tw-justify-center tw-h-12 tw-w-12 md:tw-w-auto md:tw-px-5 md:tw-gap-2 tw-rounded-full tw-border tw-border-white/20 tw-bg-white/10 hover:tw-bg-white/20 tw-text-white tw-transition-all tw-outline-none tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-shrink-0"
        >
          <FontAwesomeIcon icon={faXmark} className="tw-text-lg" />
          <span className="tw-hidden md:tw-inline tw-text-sm tw-font-bold">{t("Discard")}</span>
        </button>

        {/* SAVE BUTTON  */}
        <button
          onClick={onSave}
          disabled={isSaving}
          title={t("Save Changes")}
          className={`tw-flex tw-items-center tw-justify-center tw-gap-2 tw-h-12 tw-w-12 md:tw-w-auto md:tw-px-7 tw-rounded-full tw-font-bold tw-text-sm tw-tracking-widest tw-transition-all tw-border-none tw-outline-none tw-shrink-0 ${
            isSaving
              ? "tw-bg-[#5A3F49] tw-text-white/50 tw-cursor-not-allowed"
              : "tw-bg-[#FF43A7] tw-text-[#570033] hover:tw-bg-[#ff5cac] hover:tw-shadow-[0_0_15px_rgba(255,67,167,0.5)] tw-cursor-pointer"
          }`}
        >
          {isSaving ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="tw-animate-spin tw-text-lg" />
              <span className="tw-hidden md:tw-inline">{t("UPLOADING ASSETS...")}</span>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faFloppyDisk} className="tw-text-lg md:tw-hidden" />
              <span className="tw-hidden md:tw-inline">{t("SAVE AND EXIT")}</span>
            </>
          )}
        </button>
        
      </div>
    </div>
  );
}
